/**
 * @fileoverview UniversalChatPanel — embedded chat surface
 *
 * Self-contained chat panel that wraps the universal agent backend:
 * - Subscribes to thread messages via `@convex-dev/agent`'s
 *   `useThreadMessages` for real-time streaming.
 * - Handles optimistic user-message rendering + tool activity pills
 *   (same plumbing as `SummaryChatSheet`).
 * - Supports thread history via the universal `listJobThreads` /
 *   `listWorkspaceThreads` queries.
 *
 * Designed to be embedded inline on the Agent route (not a sheet),
 * so it shares horizontal space with the search bar / canvas. Sheet
 * mounts can be added later if needed.
 *
 * Used by:
 * - `JobAgentView`
 * - `WorkspaceAgentView`
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, type NavigateOptions } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import {
  useStreamingUIMessages,
  useThreadMessages,
} from "@convex-dev/agent/react";
import type { Message } from "@convex-dev/agent";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatMessageList,
  ChatInput,
  type ChatMessageData,
  type ChatMentionItem,
  type ChatTaggedDocument,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import {
  JobChatDocumentPicker,
  WorkspaceChatDocumentPicker,
  type ChatDocPick,
} from "./ChatDocumentPicker";
import { deriveToolInputStatus } from "@/ui/composed/chat/toolInputStatus";
import { Button } from "@/ui/primitives/button";
import {
  Plus,
  History,
  MessageSquare,
  ChevronDown,
  Check,
  PanelRightOpen,
  PanelRightClose,
} from "lucide-react";
import { ProposalReviewStack } from "./StructuredDiffCard";
import { UsageBanner } from "./UsageBanner";
import type { CitedSource } from "./sourceCitations.helpers";
import { getKindMeta } from "./searchResultKinds";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { cn } from "@/utils/misc";
import { ChartSpecSchema, type ChartSpec } from "@cvx/lib/charts/spec";
import { DiagramSpecSchema, type DiagramSpec } from "@cvx/lib/diagrams/spec";
import {
  SpreadsheetSpecSchema,
  type SpreadsheetSpec,
} from "@cvx/lib/spreadsheets/spec";
import { specToChartHtml } from "./canvas/chartHtml";
import { specToDiagramHtml } from "./canvas/diagramHtml";
import { specToSpreadsheetHtml } from "./canvas/spreadsheetHtml";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";

/** Thread messages we render — user/assistant turns only (no tool/system). */
type ChatTurnMessage = Extract<Message, { role: "user" | "assistant" }>;

/**
 * Structural view of a message part that lets the tool-call fields be read
 * across AI SDK versions (`toolName`/`args` in v4, `name`/`input` in v5/v6,
 * and both `tool-call` / `tool_call` discriminants). Every typed part is
 * assignable to it — no casting needed.
 */
type ToolCallPartView = {
  type: string;
  toolName?: string;
  name?: string;
  args?: unknown;
  input?: unknown;
};

/**
 * Pull validated chart / spreadsheet specs out of an agent message's parts.
 * `createChart` / `createSpreadsheet` return their input verbatim, so the
 * full spec lives in the tool-call part's arguments (`args` in AI SDK v4,
 * `input` in v5/v6 — read either). Persisted in thread history, so charts and
 * files re-render when a thread is reopened.
 */
function extractToolSpecs(content: ChatTurnMessage["content"]): {
  charts: ChartSpec[];
  diagrams: DiagramSpec[];
  spreadsheets: SpreadsheetSpec[];
} {
  const charts: ChartSpec[] = [];
  const diagrams: DiagramSpec[] = [];
  const spreadsheets: SpreadsheetSpec[] = [];
  if (!Array.isArray(content)) return { charts, diagrams, spreadsheets };
  for (const part of content) {
    const p: ToolCallPartView = part;
    const isToolCall = p.type === "tool-call" || p.type === "tool_call";
    if (!isToolCall) continue;
    const name = p.toolName ?? p.name;
    const raw = p.args ?? p.input;
    if (name === "createChart") {
      const r = ChartSpecSchema.safeParse(raw);
      if (r.success) charts.push(r.data);
    } else if (name === "createDiagram") {
      const r = DiagramSpecSchema.safeParse(raw);
      if (r.success) diagrams.push(r.data);
    } else if (name === "createSpreadsheet") {
      const r = SpreadsheetSpecSchema.safeParse(raw);
      if (r.success) spreadsheets.push(r.data);
    }
  }
  return { charts, diagrams, spreadsheets };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface JobScopeProps {
  scope: "job";
  jobId: Id<"jobs">;
  orgId?: never;
}
interface WorkspaceScopeProps {
  scope: "workspace";
  orgId: Id<"organizations">;
  jobId?: never;
}

export type UniversalChatPanelProps = (JobScopeProps | WorkspaceScopeProps) & {
  /**
   * Optional prefilled query (from the search bar's "Ask AI" affordance).
   * When set, the panel sends it as the first message and clears the prop
   * via `onSeedConsumed`.
   */
  seedMessage?: string;
  onSeedConsumed?: () => void;
  /**
   * Called when the agent fires its `openCanvas` tool. The parent can
   * use this to reveal a Canvas side panel. The signal is acknowledged
   * server-side via `acknowledgeCanvasOpenRequest` so the flag doesn't
   * re-trigger on every render.
   */
  onCanvasOpenRequested?: () => void;
  /**
   * When provided, the chat toolbar shows a "Show/Hide canvas" toggle
   * (next to History / New). Only the job-scope agent has a canvas —
   * workspace scope leaves these undefined and the button is hidden.
   */
  canvasOpen?: boolean;
  onToggleCanvas?: () => void;
  /** Hide the canvas panel (e.g. when starting a new thread). */
  onCanvasClose?: () => void;
  /**
   * Called whenever the active thread changes (new chat, thread switch,
   * thread created on first send). The parent uses this to point the
   * per-thread Canvas at the right thread.
   */
  onActiveThreadChange?: (threadId: string | null) => void;
  /** Restore a specific thread on mount (e.g. returning from a source link). */
  initialThreadId?: string;
  className?: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Mirrors MAX_ATTACHED_DOCS_PER_MESSAGE in convex/agents/universal/actions.ts
// (server is the source of truth).
const MAX_CHAT_ATTACHMENTS = 10;

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UniversalChatPanel(props: UniversalChatPanelProps) {
  const {
    scope,
    seedMessage,
    onSeedConsumed,
    onCanvasOpenRequested,
    canvasOpen,
    onToggleCanvas,
    onCanvasClose,
    onActiveThreadChange,
    initialThreadId,
    className,
  } = props;
  const jobId = scope === "job" ? props.jobId : undefined;
  const workspaceOrgId = scope === "workspace" ? props.orgId : undefined;

  // For job scope we need the orgId for the usage banner. Pull it
  // through the job query that the rest of the app already uses.
  const { data: job } = useQuery(
    convexQueryOrIdle(api.jobs.queries.getJob, jobId ? { jobId } : undefined),
  );
  const orgId: Id<"organizations"> | undefined = workspaceOrgId ?? job?.orgId;

  const [activeThreadId, setActiveThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const [, setIsNewThread] = useState(false);

  // Keep the parent (and thus the per-thread Canvas) pointed at the active
  // thread.
  useEffect(() => {
    onActiveThreadChange?.(activeThreadId);
  }, [activeThreadId, onActiveThreadChange]);

  // Restore a specific thread when returning from a source link.
  useEffect(() => {
    if (initialThreadId) {
      setActiveThreadId(initialThreadId);
      setIsNewThread(false);
    }
  }, [initialThreadId]);
  const [isSending, setIsSending] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [slideoutDocId, setSlideoutDocId] = useState<Id<"documents"> | null>(
    null,
  );
  // Documents attached to the draft message (picker or @-mention) — sent
  // as `attachedDocumentIds` so the backend grounds the thread in them.
  const [attachedDocs, setAttachedDocs] = useState<ChatDocPick[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();

  // Current user for avatar
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo: ChatUserInfo | undefined = useMemo(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  // Thread list — drives the History dropdown. One-query-at-a-time via
  // `convexQueryOrIdle`, same pattern as `UniversalSearchBar`.
  type ThreadInfo = {
    threadId: string;
    title?: string;
    scope: "workspace" | "job";
    jobId?: Id<"jobs">;
    orgId: Id<"organizations">;
    createdAt: number;
    updatedAt: number;
  };

  const jobThreadsQuery = useQuery(
    convexQueryOrIdle(
      api.agents.universal.publicQueries.listJobThreads,
      scope === "job" && jobId ? { jobId } : undefined,
    ),
  );
  const workspaceThreadsQuery = useQuery(
    convexQueryOrIdle(
      api.agents.universal.publicQueries.listWorkspaceThreads,
      scope === "workspace" && workspaceOrgId
        ? { orgId: workspaceOrgId }
        : undefined,
    ),
  );
  const threads: ThreadInfo[] =
    (scope === "job" ? jobThreadsQuery.data : workspaceThreadsQuery.data) ?? [];

  // Documents offered by the composer's @-mention dropdown. Job scope
  // reuses the library listing; workspace scope uses the bounded
  // cross-job listing (same source as the workspace picker).
  const { data: jobDocs } = useQuery(
    convexQueryOrIdle(
      api.jobs.gatherInformation.documents.queries.listDocuments,
      scope === "job" && jobId ? { jobId } : undefined,
    ),
  );
  const { data: workspaceDocs } = useQuery(
    convexQueryOrIdle(
      api.agents.universal.publicQueries.listWorkspaceAttachableDocuments,
      scope === "workspace" && workspaceOrgId
        ? { orgId: workspaceOrgId }
        : undefined,
    ),
  );

  const mentionDocById = useMemo(() => {
    const map = new Map<string, ChatDocPick>();
    if (scope === "job") {
      for (const doc of jobDocs ?? []) {
        map.set(doc._id, {
          documentId: doc._id,
          fileName: doc.fileName,
          jobId: doc.jobId,
        });
      }
    } else {
      for (const doc of workspaceDocs ?? []) {
        map.set(doc.documentId, {
          documentId: doc.documentId,
          fileName: doc.fileName,
          jobId: doc.jobId,
          jobLabel: doc.jobLabel,
        });
      }
    }
    return map;
  }, [scope, jobDocs, workspaceDocs]);

  const mentionItems: ChatMentionItem[] = useMemo(
    () =>
      [...mentionDocById.values()].map((doc) => ({
        id: doc.documentId,
        name: doc.fileName,
        subtitle: doc.jobLabel,
      })),
    [mentionDocById],
  );

  // Same documents, message-history shape — lets @-mention and Attached
  // chips in sent user bubbles resolve to the document detail slideout.
  const taggedDocuments: ChatTaggedDocument[] = useMemo(
    () =>
      [...mentionDocById.values()].map((doc) => ({
        id: doc.documentId,
        name: doc.fileName,
      })),
    [mentionDocById],
  );

  const addAttachedDocs = useCallback((picked: ChatDocPick[]) => {
    setAttachedDocs((prev) => {
      const seen = new Set<string>(prev.map((d) => d.documentId));
      const merged = [
        ...prev,
        ...picked.filter((d) => !seen.has(d.documentId)),
      ];
      if (merged.length > MAX_CHAT_ATTACHMENTS) {
        toast.error(
          `You can attach at most ${MAX_CHAT_ATTACHMENTS} files to one message`,
        );
        return merged.slice(0, MAX_CHAT_ATTACHMENTS);
      }
      return merged;
    });
  }, []);

  const handleMentionSelect = useCallback(
    (item: ChatMentionItem) => {
      const pick = mentionDocById.get(item.id);
      if (pick) addAttachedDocs([pick]);
    },
    [mentionDocById, addAttachedDocs],
  );

  const handlePickerSelect = useCallback(
    (picked: ChatDocPick[]) => addAttachedDocs(picked),
    [addAttachedDocs],
  );

  // Both scopes start with a blank new-chat state; previous threads are
  // accessible via the History dropdown.

  // Subscribe to messages on the active thread.
  const threadMessages = useThreadMessages(
    api.agents.universal.publicQueries.listMessagesForThread,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  const isLoadingThread =
    !!activeThreadId && threadMessages?.status === "LoadingFirstPage";

  // Streaming UIMessages expose partial tool-call input (state
  // "input-streaming") that the MessageDoc mapping above drops — this is
  // the only signal that the model is mid-way through writing a large
  // tool argument (e.g. the canvas HTML), which is the slowest part of a
  // canvas turn. Rides the same query/args as `useThreadMessages`, so the
  // underlying Convex subscriptions are shared.
  const streamingUIMessages = useStreamingUIMessages(
    api.agents.universal.publicQueries.listMessagesForThread,
    activeThreadId
      ? {
          threadId: activeThreadId,
          // The hook only reads the `streams` half of the response — an
          // empty page keeps the shared query shape happy (same trick
          // useThreadMessages uses internally for its stream leg).
          paginationOpts: { cursor: null, numItems: 0 },
        }
      : "skip",
  );
  const activityStatus = useMemo(
    () => deriveToolInputStatus(streamingUIMessages),
    [streamingUIMessages],
  );

  // Pending agent-proposed mutations for this thread. Reactive so the
  // Apply / Reject card appears as soon as the agent's `propose*` tool
  // call completes.
  const { data: pendingProposals } = useQuery({
    ...convexQuery(
      api.agents.universal.proposals.listPendingProposalsForThread,
      { threadId: activeThreadId ?? "" },
    ),
    enabled: !!activeThreadId,
  });

  // Reactive signal from the agent's `openCanvas` tool — surface to the
  // parent so it can reveal the Canvas panel. We ack the signal once
  // we've notified the parent so the flag doesn't keep re-firing.
  const { data: canvasOpenRequested } = useQuery({
    ...convexQuery(api.agents.universal.publicQueries.getCanvasOpenRequested, {
      threadId: activeThreadId ?? undefined,
    }),
    enabled: !!activeThreadId,
  });
  const { mutateAsync: ackCanvasOpenRequest } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.mutations.acknowledgeCanvasOpenRequest,
    ),
  });
  useEffect(() => {
    if (canvasOpenRequested && activeThreadId) {
      onCanvasOpenRequested?.();
      void ackCanvasOpenRequest({ threadId: activeThreadId });
    }
  }, [
    canvasOpenRequested,
    activeThreadId,
    onCanvasOpenRequested,
    ackCanvasOpenRequest,
  ]);

  // Defence in depth — if the agent staged a canvas_patch proposal but
  // forgot to call `openCanvas` first, auto-reveal the panel anyway so
  // the user never has to manually toggle it to see what was drafted.
  const hasPendingCanvasPatch = useMemo(
    () => (pendingProposals ?? []).some((p) => p.kind === "canvas_patch"),
    [pendingProposals],
  );
  useEffect(() => {
    if (hasPendingCanvasPatch) onCanvasOpenRequested?.();
  }, [hasPendingCanvasPatch, onCanvasOpenRequested]);

  // Save-to-canvas: append a chart block to the active thread's canvas and
  // reveal the panel. Purely client-side — no agent round-trip.
  const { data: threadCanvas } = useQuery({
    ...convexQuery(api.agents.universal.canvas.getMyThreadCanvas, {
      threadId: activeThreadId ?? undefined,
    }),
    enabled: !!activeThreadId,
  });
  const { mutateAsync: upsertThreadCanvas } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.canvas.upsertThreadCanvas,
    ),
  });
  const handleSaveChartToCanvas = useCallback(
    async (spec: ChartSpec) => {
      if (!activeThreadId) return;
      const currentHtml = threadCanvas?.html ?? "";
      try {
        await upsertThreadCanvas({
          threadId: activeThreadId,
          html: currentHtml + specToChartHtml(spec),
        });
      } catch (err) {
        console.error("[UniversalChatPanel] save chart to canvas failed", err);
        toast.error("Could not save the chart to your canvas");
        throw err;
      }
      onCanvasOpenRequested?.();
    },
    [
      activeThreadId,
      threadCanvas?.html,
      upsertThreadCanvas,
      onCanvasOpenRequested,
    ],
  );
  const handleSaveDiagramToCanvas = useCallback(
    async (spec: DiagramSpec) => {
      if (!activeThreadId) return;
      const currentHtml = threadCanvas?.html ?? "";
      try {
        await upsertThreadCanvas({
          threadId: activeThreadId,
          html: currentHtml + specToDiagramHtml(spec),
        });
      } catch (err) {
        console.error(
          "[UniversalChatPanel] save diagram to canvas failed",
          err,
        );
        toast.error("Could not save the diagram to your canvas");
        throw err;
      }
      onCanvasOpenRequested?.();
    },
    [
      activeThreadId,
      threadCanvas?.html,
      upsertThreadCanvas,
      onCanvasOpenRequested,
    ],
  );
  const handleSaveSpreadsheetToCanvas = useCallback(
    async (spec: SpreadsheetSpec) => {
      if (!activeThreadId) return;
      const currentHtml = threadCanvas?.html ?? "";
      try {
        await upsertThreadCanvas({
          threadId: activeThreadId,
          html: currentHtml + specToSpreadsheetHtml(spec),
        });
      } catch (err) {
        console.error(
          "[UniversalChatPanel] save spreadsheet to canvas failed",
          err,
        );
        toast.error("Could not save the spreadsheet to your canvas");
        throw err;
      }
      onCanvasOpenRequested?.();
    },
    [
      activeThreadId,
      threadCanvas?.html,
      upsertThreadCanvas,
      onCanvasOpenRequested,
    ],
  );

  // Send action — switches on scope.
  const { mutateAsync: sendJobAction } = useMutation({
    mutationFn: useConvexAction(
      api.agents.universal.actions.sendJobChatMessage,
    ),
  });
  const { mutateAsync: sendWorkspaceAction } = useMutation({
    mutationFn: useConvexAction(
      api.agents.universal.actions.sendWorkspaceChatMessage,
    ),
  });

  const handleSend = useCallback(
    async (message: string, overrideThreadId?: string | null) => {
      const threadId =
        overrideThreadId === null
          ? undefined
          : (overrideThreadId ?? activeThreadId ?? undefined);
      // Make picker-attached files visible in the message history too —
      // @-mentioned files are already named in the text, so only list the
      // rest. The suffix is part of the stored message (no hidden state).
      const unmentioned = attachedDocs.filter(
        (d) => !message.includes(`@${d.fileName}`),
      );
      const finalMessage =
        unmentioned.length > 0
          ? `${message}\n\nAttached: ${unmentioned.map((d) => d.fileName).join(", ")}`
          : message;
      const attachedDocumentIds =
        attachedDocs.length > 0
          ? attachedDocs.map((d) => d.documentId)
          : undefined;
      setPendingMessage(finalMessage);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        const result: { threadId: string } =
          scope === "job"
            ? await sendJobAction({
                jobId: jobId as Id<"jobs">,
                message: finalMessage,
                threadId,
                attachedDocumentIds,
              })
            : await sendWorkspaceAction({
                orgId: orgId as Id<"organizations">,
                message: finalMessage,
                threadId,
                attachedDocumentIds,
              });
        setAttachedDocs([]);
        if (!threadId) {
          setActiveThreadId(result.threadId);
          setIsNewThread(false);
        }
      } catch (err) {
        console.error("[UniversalChatPanel] send failed", err);
        toast.error(extractErrorMessage(err, "Could not send the message"));
        setPendingMessage(null);
        setWaitingForResponse(false);
      } finally {
        setTimeout(() => setIsSending(false), 500);
      }
    },
    [
      scope,
      jobId,
      orgId,
      activeThreadId,
      attachedDocs,
      sendJobAction,
      sendWorkspaceAction,
    ],
  );

  // Consume an external seed message exactly once. Always start a fresh
  // thread so queries from the Cmd+K palette don't land in an existing
  // conversation. Pass `null` as overrideThreadId to bypass the stale
  // closure over `activeThreadId`.
  useEffect(() => {
    if (seedMessage && seedMessage.trim().length > 0) {
      setActiveThreadId(null);
      setIsNewThread(true);
      handleSend(seedMessage.trim(), null);
      onSeedConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume-once seed: must fire only when a new seedMessage arrives; depending on handleSend/onSeedConsumed (fresh identities each render) would re-send the seed on unrelated re-renders.
  }, [seedMessage]);

  // Map agent messages to ChatMessageData (same logic as SummaryChatSheet).
  //
  // Defensively reject tool-result payloads that the SDK sometimes
  // normalises into a plain `user`-role string. They show up in the
  // raw thread as a JSON-stringified array (e.g. the output of
  // `searchAcrossJob` — `[{"kind":"document","id":"..."}, …]`) and,
  // without filtering, render as a giant unreadable user bubble.
  // Real tool_result parts (`type === "tool_result"`) are already
  // dropped by the text-part check below.
  function looksLikeToolResultDump(s: string): boolean {
    const t = s.trim();
    if (t.length < 8) return false;
    if (!t.startsWith("[") && !t.startsWith("{")) return false;
    if (!t.endsWith("]") && !t.endsWith("}")) return false;
    // Quick structural sniff — tool dumps almost always contain a
    // key like "kind" / "id" / "_id" / "table". Avoid false positives
    // for legitimate JSON the user might paste by also requiring at
    // least one quoted-key pattern.
    return /"(kind|id|_id|table|jobId|orgId)"\s*:/i.test(t);
  }
  const rawMessages = threadMessages?.results ?? [];
  type RawThreadMessage = (typeof rawMessages)[number];

  const realMessages: ChatMessageData[] = rawMessages
    .filter((m): m is RawThreadMessage & { message: ChatTurnMessage } => {
      const message = m.message;
      if (message?.role !== "user" && message?.role !== "assistant")
        return false;
      const content = message.content;
      if (!content) return false;
      if (typeof content === "string") {
        const trimmed = content.trim();
        if (trimmed.length === 0) return false;
        if (message.role === "user" && looksLikeToolResultDump(trimmed)) {
          return false;
        }
        return true;
      }
      if (Array.isArray(content)) {
        const hasRenderableText = content.some(
          (part) =>
            part.type === "text" &&
            part.text?.trim() &&
            !(message.role === "user" && looksLikeToolResultDump(part.text)),
        );
        if (hasRenderableText) return true;
        // Keep assistant turns that produced only a chart / diagram /
        // spreadsheet (a tool-call part, no prose) so the inline card
        // still renders.
        const { charts, diagrams, spreadsheets } = extractToolSpecs(content);
        return (
          charts.length > 0 || diagrams.length > 0 || spreadsheets.length > 0
        );
      }
      return false;
    })
    .map((m) => {
      const content = m.message.content;
      const text =
        typeof content === "string"
          ? content
          : content
              .flatMap((p) => (p.type === "text" ? [p.text] : []))
              .join("");
      const { charts, diagrams, spreadsheets } = extractToolSpecs(content);
      return {
        id: m.key ?? m._id ?? String(m._creationTime),
        role: m.message.role,
        content: text,
        createdAt: m._creationTime,
        isStreaming: m.streaming === true,
        charts: charts.length ? charts : undefined,
        diagrams: diagrams.length ? diagrams : undefined,
        spreadsheets: spreadsheets.length ? spreadsheets : undefined,
      };
    });

  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Derive thinking + tool state from the raw thread (covers the FULL
  // tool-use cycle so the activity pills don't blink out mid-loop).
  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = threadMessages?.results ?? [];
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }
    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      if (m.message?.role === "assistant" && Array.isArray(m.message.content)) {
        for (const part of m.message.content) {
          if (part.type === "tool-call" && part.toolName) {
            tools.push(part.toolName);
          }
        }
      }
    }
    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;
    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }
    const lastContent = lastRaw.message?.content;
    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(lastContent) &&
      lastContent.some((p) => p.type === "tool-call");
    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;
    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) setWaitingForResponse(false);
  }, [waitingForResponse, isAgentThinking]);

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsNewThread(true);
    setAttachedDocs([]);
    onCanvasClose?.();
  }, [onCanvasClose]);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setIsNewThread(false);
    setAttachedDocs([]);
  }, []);

  const handleSourceClick = useCallback(
    (source: CitedSource): boolean => {
      if (source.kind === "document" && source.id) {
        setSlideoutDocId(source.id as Id<"documents">);
        return true;
      }
      const meta = getKindMeta(source.kind);
      const target = meta.buildTo({
        id: source.id,
        jobId: source.jobId ?? jobId,
        orgId: source.orgId,
      });
      if (target) {
        // `buildTo` returns runtime-assembled route strings, so the target
        // can't satisfy the router's statically-typed routes — funnel it
        // through the router's own option type instead of `any`.
        void navigate({
          to: target.to,
          params: target.params,
          search: {
            ...target.search,
            from: "chat",
            ...(activeThreadId ? { threadId: activeThreadId } : {}),
          },
        } as NavigateOptions);
        return true;
      }
      return false;
    },
    [jobId, navigate, activeThreadId],
  );

  const emptyHeadline =
    scope === "job"
      ? "What can Morgan help with?"
      : "Ask Morgan anything across your workspace";
  const emptyMessage =
    scope === "job"
      ? "Ask questions about your job; search for job information and prepare documents using canvas."
      : "Ask questions across all your jobs. Morgan references the source job and any data for each answer.";

  // ChatGPT-style quick prompts shown only on the empty state.
  // Clicking one seeds the chat composer via `handleSend`.
  const suggestions =
    scope === "job"
      ? [
          {
            label: "Summarise the assets and liabilities",
            prompt: "Summarise the assets and liabilities for this job.",
          },
          {
            label: "Summarise any director-related dealings",
            prompt: "Summarise any director-related dealings for this job.",
          },
          {
            label: "Who are the secured creditors and what amounts are owing",
            prompt: "Who are the secured creditors and what amounts are owing?",
          },
          {
            label: "List the key records that are missing from this job",
            prompt: "List the key records that are missing from this job.",
          },
        ]
      : [
          {
            label: "Give me a summary of my active jobs",
            prompt: "Give me a summary of my active jobs.",
          },
          {
            label: "Summarise the assets across my active jobs",
            prompt: "Summarise the assets across my active jobs.",
          },
          {
            label: "Break my jobs down by appointment type",
            prompt: "Break my jobs down by appointment type.",
          },
          {
            label: "Analyse my jobs by industry type",
            prompt: "Analyse my jobs by industry type.",
          },
        ];

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      {/* Header toolbar */}
      <div className="border-b border-border bg-background/80 px-5 pb-3 pt-4 backdrop-blur-sm">
        <div className="flex items-center justify-end">
          <div className="flex shrink-0 items-center gap-1.5">
            {onToggleCanvas && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCanvas}
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                title={canvasOpen ? "Hide canvas" : "Show canvas"}
              >
                {canvasOpen ? (
                  <PanelRightClose className="h-3.5 w-3.5" />
                ) : (
                  <PanelRightOpen className="h-3.5 w-3.5" />
                )}
                {canvasOpen ? "Hide canvas" : "Canvas"}
              </Button>
            )}
            {threads.length > 0 && (
              <DropdownMenu open={historyOpen} onOpenChange={setHistoryOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <History className="h-3.5 w-3.5" />
                    History
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-2">
                  <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                    Previous conversations
                  </p>
                  <div className="max-h-[320px] space-y-0.5 overflow-y-auto">
                    {threads.map((thread) => (
                      <button
                        key={thread.threadId}
                        type="button"
                        onClick={() => {
                          handleSelectThread(thread.threadId);
                          setHistoryOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors",
                          thread.threadId === activeThreadId
                            ? "bg-accent/20 text-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                      >
                        <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-50" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium leading-tight">
                            {thread.title ?? "New conversation"}
                          </p>
                          <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                            {formatRelativeTime(thread.updatedAt)}
                          </p>
                        </div>
                        {thread.threadId === activeThreadId && (
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewThread}
              className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              title="Start new conversation"
            >
              <Plus className="h-3.5 w-3.5" />
              New
            </Button>
          </div>
        </div>
      </div>

      {/* Messages — `min-h-0` + `flex-1` is the magic combo that lets
          the message list shrink inside the parent flex column so the
          composer + proposal cards + usage banner stay visible. The
          internal `overflow-y-auto` handles its own scrolling. */}
      <ChatMessageList
        messages={messages}
        userInfo={userInfo}
        taggedDocuments={taggedDocuments}
        onDocumentClick={(documentId) =>
          setSlideoutDocId(documentId as Id<"documents">)
        }
        isLoading={isLoadingThread}
        isThinking={isAgentThinking}
        activeToolCalls={activeToolCalls}
        activityStatus={activityStatus}
        emptyHeadline={emptyHeadline}
        emptyMessage={emptyMessage}
        suggestions={suggestions}
        onSuggestionClick={handleSend}
        onSourceClick={handleSourceClick}
        onSaveChart={handleSaveChartToCanvas}
        onSaveDiagram={handleSaveDiagramToCanvas}
        onSaveSpreadsheet={handleSaveSpreadsheetToCanvas}
        className="min-h-0 flex-1 bg-muted/20"
      />

      {/* Pending agent-proposed changes — Apply / Reject card stack with
          a bulk Apply all / Reject all bar when more than one is staged. */}
      {pendingProposals && pendingProposals.length > 0 && activeThreadId && (
        <div className="max-h-[360px] overflow-y-auto border-t border-border bg-background/80 py-1">
          <ProposalReviewStack
            proposals={pendingProposals}
            scope={{ kind: "thread", threadId: activeThreadId }}
          />
        </div>
      )}

      {/* Usage banner — $80 soft warn / $100 hard block. Reactive so
          the message updates as soon as the org rolls into a new
          period or hits the cap mid-session. */}
      {orgId && <UsageBanner orgId={orgId} />}

      {/* Chat input — matches the ChatInput used in summary chats, plus
          the attach button + @-mention document tagging (MOR-890). */}
      <ChatInput
        onSend={handleSend}
        isDisabled={isSending}
        placeholder={
          scope === "job"
            ? "Ask about this job…"
            : "Ask Morgan anything across all your jobs..."
        }
        attachments={attachedDocs.map((d) => ({
          id: d.documentId,
          name: d.fileName,
          subtitle: scope === "workspace" ? d.jobLabel : undefined,
        }))}
        onRemoveAttachment={(id) =>
          setAttachedDocs((prev) => prev.filter((d) => d.documentId !== id))
        }
        onAttachClick={() => setPickerOpen(true)}
        mentionItems={mentionItems}
        onMentionSelect={handleMentionSelect}
      />

      {scope === "job" && jobId && (
        <JobChatDocumentPicker
          jobId={jobId}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handlePickerSelect}
        />
      )}
      {scope === "workspace" && workspaceOrgId && (
        <WorkspaceChatDocumentPicker
          orgId={workspaceOrgId}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handlePickerSelect}
        />
      )}

      <DocumentDetail
        documentId={slideoutDocId}
        isViewer={false}
        open={!!slideoutDocId}
        onClose={() => setSlideoutDocId(null)}
      />
    </div>
  );
}
